import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";

/*
 * เช็คอินรายวัน — ตรรกะฝั่งลูกค้า (ใช้ร่วมกันทั้ง landing และ landing-ver2; ไฟล์นี้เหมือนกันทั้งสองแอป)
 * หน้าตาแยกกันที่ pages/Checkin.js (landing = สมุดแสตมป์ · landing-ver2 = เส้นทางรางวัลนีออน)
 *   • เซิร์ฟเวอร์ตัดสินทุกอย่าง (วันนี้เช็คอินแล้วไหม วันที่เท่าไหร่ รางวัล เทิร์น เปิด-ปิดระบบ) — ฝั่งนี้แค่แสดงผลและส่งคำสั่งเช็คอิน
 */

const nf = (v) => Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });

/** รางวัลของวัน → { text: "5 เพชร" | "฿20", icon, cash } */
export function rewardInfo(d) {
  if (!d) return { text: "", icon: "", cash: false };
  const cash = d.reward_type === "cashWallet";
  return cash
    ? { text: `฿${nf(d.amount)}`, short: `฿${nf(d.amount)}`, icon: "fluent-emoji-flat:money-bag", cash: true }
    : { text: `${nf(d.amount)} เพชร`, short: nf(d.amount), icon: "fluent-emoji-flat:gem-stone", cash: false };
}

/** รูปไอคอนกิจกรรม (path จากเซิร์ฟเวอร์ เช่น /checkin-assets/calendar.svg หรือ /uploads/x.png) → URL เต็ม */
export const eventImg = (url) => {
  const u = String(url || "");
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${lottoApi.base || ""}${u.startsWith("/") ? u : `/${u}`}`;
};

export const turnPct = (a) => (a && a.turnover_required > 0 ? Math.max(0, Math.min(100, (a.turnover_done / a.turnover_required) * 100)) : 0);

/** ข้อความสถานะปุ่มหลัก */
export function buttonState(view, busy) {
  if (!view.enabled) return { disabled: true, label: "ระบบเช็คอินปิดให้บริการชั่วคราว" };
  if (busy) return { disabled: true, label: "กำลังเช็คอิน..." };
  switch (view.state) {
    case "READY":
      return { disabled: false, label: (view.event && view.event.button_text) || "เช็คอินเลย" };
    case "DONE_TODAY":
      return { disabled: true, label: "วันนี้เช็คอินแล้ว" };
    case "COMPLETED":
      return { disabled: true, label: "เช็คอินครบทุกวันแล้ว" };
    case "NOT_STARTED":
      return { disabled: true, label: "กิจกรรมยังไม่เริ่ม" };
    case "ENDED":
      return { disabled: true, label: "กิจกรรมสิ้นสุดแล้ว" };
    default:
      return { disabled: true, label: "ยังไม่มีกิจกรรมเช็คอิน" };
  }
}

/** วินาที → "05:12:09" */
export const clockText = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
};

/** นับถอยหลังถึงเที่ยงคืน (เริ่มจาก seconds_to_reset ที่เซิร์ฟเวอร์ส่งมา — ไม่เชื่อนาฬิกาเครื่องลูกค้า) เหลือ 0 → เรียก onZero ให้โหลดใหม่ */
export function useResetClock(seconds, onZero) {
  const [left, setLeft] = useState(seconds || 0);
  const cb = useRef(onZero);
  cb.current = onZero;
  useEffect(() => {
    setLeft(seconds || 0);
    if (!(seconds > 0)) return undefined;
    const startedAt = Date.now();
    const id = setInterval(() => {
      const remain = Math.max(0, seconds - Math.floor((Date.now() - startedAt) / 1000));
      setLeft(remain);
      if (remain === 0) {
        clearInterval(id);
        if (cb.current) cb.current();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);
  return left;
}

export { whenText } from "./creditCodeLogic";

const EMPTY = { enabled: true, state: "NO_EVENT", event: null, days: [], progress: null, today_claimed: false, seconds_to_reset: 0, totals: { diamonds: 0, cash: 0, days: 0 }, rules: [], play_label: "", history: [], active: [] };

export function useCheckin(onDone) {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [view, setView] = useState(null); // null = ยังโหลดไม่เสร็จ
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok:true, ...claim } | { ok:false, message }
  const alive = useRef(true);
  const inFlight = useRef(false);
  const cb = useRef(onDone);
  cb.current = onDone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!uid) return Promise.resolve();
    return Promise.resolve()
      .then(() => lottoApi.myCheckin())
      .then((v) => alive.current && setView({ ...EMPTY, ...(v || {}) }))
      .catch(() => alive.current && setView((prev) => prev || { ...EMPTY, failed: true }));
  }, [uid]);

  useEffect(() => {
    setView(null);
    setResult(null);
    load();
    const onVis = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  const claim = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await lottoApi.claimCheckin();
      if (alive.current) setResult({ ok: true, ...(res?.data || {}) });
      if (cb.current) cb.current(res?.data);
    } catch (err) {
      const d = err?.response?.data;
      if (alive.current) setResult({ ok: false, code: d?.code || "ERROR", message: d?.message || "เช็คอินไม่สำเร็จ ลองใหม่อีกครั้ง" });
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
      load();
    }
  }, [load]);

  const v = view || EMPTY;
  return {
    isAuthenticated,
    loading: !!uid && view === null,
    failed: !!(view && view.failed),
    view: v,
    enabled: v.enabled !== false,
    busy,
    claim,
    result,
    closeResult: () => setResult(null),
    reload: load,
  };
}
